import React, { useEffect, useState } from "react";
import "./InitialAssessmentReport.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faPen, faDownload } from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router";

const InitialAssessmentReport = () => {
  const { claimNumber } = useParams();
  const [formData, setFormData] = useState({
    claim_number: "",
    email_id: "",
    phone_number: "",
    created_at: "",
    NameofDriver: "",
    dateTimeOfSurvey: "",
    placeOfSurvey: "",
    driverLicenseNo: "",
    driverAge: "",
    driverExperience: "",
    driverValidLicense: "",
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();
  const [claimData, setClaimData] = useState(null);

  // Format date to DD-MM-YYYY HH:MM:SS
  const formatDateTime = (dateString) => {
    if (!dateString) return "--";

    try {
      const date = new Date(dateString);
      if (isNaN(date)) return "--";

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "--";
    }
  };

  useEffect(() => {
    const fetchClaimDetails = async () => {
      try {
        const res = await axios.post("/process_data/claim-data", {
          claimNumber,
        });
        console.log("391", res);
        setClaimData(res.data);
      } catch (err) {
        console.error("Error fetching claim details:", err);
      }
    };

    if (claimNumber) {
      fetchClaimDetails();
    }
  }, [claimNumber]);

  // Enhanced handleApproveClick with PDF download
  const handleApproveClick = async () => {
    try {
      setDownloading(true);

      // First try direct PDF download
      try {
        const response = await axios.get(
          `/api/generate-initial-assessment-pdf/${claimNumber}`,
          {
            responseType: "blob",
            timeout: 30000,
          },
        );

        if (response.status === 200) {
          // Create and trigger download
          const blob = new Blob([response.data], { type: "application/pdf" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Initial_Assessment_Report_${claimNumber}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          console.log("PDF downloaded successfully");

          // Update approval status in backend
          await updateApprovalStatus();

          // Navigate to next page after successful download
          navigate(`/initial-report-manual/${claimNumber}`, {
            state: { approved: true, claimNumber },
          });
          return;
        }
      } catch (downloadError) {
        console.log("Direct download failed, using fallback:", downloadError);
      }

      // Fallback: Navigate without download
      await updateApprovalStatus();
      navigate(`/initial-report-manual/${claimNumber}`, {
        state: { approved: true, claimNumber },
      });
    } catch (error) {
      console.error("Approval process failed:", error);
      alert("Approval process failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Update approval status in backend
  const updateApprovalStatus = async () => {
    try {
      await axios.post("/process_data/update_workapproval_status", {
        claimNumber: claimNumber,
        status: "approved",
        approvedAt: new Date().toISOString(),
        approvedBy: "Surveyor AI",
      });
      console.log("Approval status updated successfully");
    } catch (error) {
      console.error("Failed to update approval status:", error);
    }
  };

  const handleManualqcClick = () => {
    navigate(`/manual-qc/${claimNumber}`);
  };

  // Enhanced data fetching - Updated to match second component's structure
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log("Fetching data for claim:", claimNumber);

        // Fetch claim details
        const claimRes = await axios.post("/process_data/claim-details", {
          claimNumber,
        });
        console.log("Claim Details:", claimRes.data);
        setFormData(claimRes.data);

        // Fetch assessment data using the same endpoint as second component
        const assessmentRes = await axios.post(
          `/server/get-claim-data/${claimNumber}`,
        );
        console.log("Assessment Data:", assessmentRes.data);
        setData(assessmentRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);

        // Fallback: Create mock data if API fails - Updated structure
        setData({
          policyNumber: "POL" + (claimNumber || "").slice(-6),
          claimNumber: claimNumber,
          date: formatDateTime(new Date()),
          insured: "Policy Holder Name",
          EngineNumber: "ENG" + Math.random().toString(36).substr(2, 9),
          hpa: "Authorized Service Center",
          VehicleRegistrationNumber: "DL01AB1234",
          DateofRegistration: "2022-01-15",
          dateOfAccident: formatDateTime(
            new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          ),
          Make: "Maruti Suzuki",
          Variant: "Swift Dzire",
          ChassisNumber: "CHS" + Math.random().toString(36).substr(2, 9),
          parts: [
            {
              decision: "Replace",
              material: "Steel",
              description: "Front Bumper",
              hsn: "8708",
              qty: "1",
              rate: "4500",
              taxableAmount: "4500",
              taxPercent: "18",
              taxAmount: "810",
              totalAmount: "5310",
              depPercent: "10",
              depAmount: "450",
              rrCharges: "500",
              repairCharges: "0",
              paintingCharges: "1200",
            },
          ],
          labourDetails: [
            {
              labourType: "Mechanical",
              taxableAmount: "1500",
              taxAmount: "270",
              totalAmount: "1770",
              depAmount: "150",
            },
          ],
          finalCalculation: {
            totalLiability: "10856",
            compulsoryDeductible: "500",
            voluntaryDeductible: "0",
            salvageDeductions: "0",
            nonStandardAmount: "0",
            towingAmount: "800",
            digitLiability: "9556",
          },
        });
      } finally {
        setLoading(false);
      }
    };

    if (claimNumber) {
      fetchData();
    }
  }, [claimNumber]);
  const paintingSubtotal =
    data?.parts?.reduce((sum, p) => {
      return sum + (Number(p.paintingCharges) || 0);
    }, 0) || 0;

  const labourTaxPercent = 18;
  const labourTaxAmount = (paintingSubtotal * labourTaxPercent) / 100;
  const labourTotalAmount = paintingSubtotal + labourTaxAmount;
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Assessment Report...</p>
      </div>
    );
  }
  const getSubtotal = (key) => {
    return (
      data?.parts?.reduce((sum, p) => {
        return sum + (Number(p[key]) || 0);
      }, 0) || 0
    );
  };

  const getDepAmount = (key) => {
    return (
      data?.parts?.reduce((sum, p) => {
        const value = Number(p[key]) || 0;
        return sum + (value * Number(p.depPercent || 0)) / 100;
      }, 0) || 0
    );
  };
  const getLabourFromAPI = (label) => {
    return data?.labourDetails?.find((l) => l.labourType === label);
  };

  const labourTypes = [
    { label: "Painting Charges", key: "paintingCharges" },
    { label: "Repair Charges", key: "repairCharges" },
    { label: "R&R Charges", key: "rrCharges" },
  ];

  if (!data) {
    return (
      <div className="error-container">
        <h3>Error Loading Report</h3>
        <p>Unable to load assessment data for claim {claimNumber}</p>
        <button onClick={() => window.location.reload()} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }
  // const partsTotal =
  //   data?.parts?.reduce((sum, p) => {
  //     const base = Number(p.qty) * Number(p.rate);
  //     const tax = (base * Number(p.taxPercent || 0)) / 100;
  //     return sum + base + tax;
  //   }, 0) || 0;
  // const labourKeys = ["paintingCharges", "repairCharges", "rrCharges"];

  // const labourTotal = labourKeys.reduce((sum, key) => {
  //   const taxable =
  //     data?.parts?.reduce((s, p) => s + (Number(p[key]) || 0), 0) || 0;

  //   const tax = (taxable * 18) / 100;

  //   return sum + taxable + tax;
  // }, 0);
  const labourKeys = ['paintingCharges', 'repairCharges', 'rrCharges'];

const labourTotal = labourKeys.reduce((sum, key) => {
  const taxable =
    data?.parts?.reduce((s, p) => s + (Number(p[key]) || 0), 0) || 0;

  const tax = (taxable * 18) / 100;

  // ✅ fetch dep from API (same as table)
  const apiLabour = data?.labourDetails?.find((l) =>
    l.labourType?.toLowerCase().includes(key.replace('Charges', '').toLowerCase())
  );

  const dep =
    apiLabour?.depAmount && apiLabour.depAmount !== "Not Found"
      ? Number(apiLabour.depAmount)
      : 0;

  // ✅ updated total
  const total = taxable + tax - dep;

  return sum + total;
}, 0);
  const partsTotal =
  data?.parts?.reduce((sum, p) => {
    const base = Number(p.qty) * Number(p.rate);
    const tax = (base * Number(p.taxPercent || 0)) / 100;
    return sum + base + tax;
  }, 0) || 0;
  const totalLiability = partsTotal + labourTotal;
  const compulsoryDeductible =
    data?.finalCalculation?.compulsoryDeductible &&
    data.finalCalculation.compulsoryDeductible !== "Not Found"
      ? Number(data.finalCalculation.compulsoryDeductible)
      : 0;

  // Part depreciation total
const partsDepTotal =
  data?.parts?.reduce((sum, p) => {
    const base = Number(p.qty) * Number(p.rate);
    return sum + (base * Number(p.depPercent || 0)) / 100;
  }, 0) || 0;

// Labour depreciation total
const labourDepTotal =
  data?.labourDetails?.reduce((sum, l) => {
    return (
      sum +
      (l.depAmount && l.depAmount !== "Not Found"
        ? Number(l.depAmount)
        : 0)
    );
  }, 0) || 0;

// API depreciation
const apiDepreciation =
  Number(data?.finalCalculation?.depriciationAmount || 0);

// Final depreciation
const totalDepreciation =
  apiDepreciation +
  partsDepTotal +
  labourDepTotal;

// Nil depreciation
const nilDepreciationAmount =
  Number(data?.finalCalculation?.nilDepriciationAmount || 0);    

  // const insurerLiability = totalLiability - compulsoryDeductible;
  let insurerLiability =
  totalLiability -
  compulsoryDeductible +
  totalDepreciation;

if (nilDepreciationAmount > 0) {
  insurerLiability += nilDepreciationAmount;
} else if (nilDepreciationAmount < 0) {
  insurerLiability -= Math.abs(nilDepreciationAmount);
}
  return (
    <div className="report-container">
      <div className="AI-recommendation">
        <p>AI Recommendation: Claim Payable | Claim Non Payable</p>
      </div>

      <div className="report-header">
        <img
          src="/custom-icons/surveyorIcon.png"
          alt="Surveyor AI Logo"
          className="logo"
        />

        <div className="report-title">
          <h2>Initial Assessment Report</h2>
        </div>

        <p className="printed-date">Printed: {formatDateTime(new Date())}</p>
      </div>

      <div className="inline-box-row">
        <div className="inline-box label">Policy Number</div>
        <div className="inline-box value">{data.policyNumber}</div>
        <div className="inline-box label">Claim Number</div>
        <div className="inline-box value">{data.claimNumber}</div>
        <div className="inline-box label">Ticket Number</div>
        <div className="inline-box value">--</div>
      </div>

      <div className="inline-box-row info-box">
        <div className="info-full-row">
          <div className="inline-box label">
            Date:{formatDateTime(new Date())}
          </div>
          <div className="inline-box value">{claimData?.date || "--"}</div>
        </div>

        <div className="info-row">
          <div className="inline-box label">Name of Owner:</div>
          <div className="inline-box value">
            {data?.insured || claimData?.customer_name || "--"}
          </div>

          <div className="inline-box label">Engine No.:</div>
          <div className="inline-box value">
            {data?.EngineNumber || claimData?.engine_number || "--"}
          </div>

          <div className="inline-box label">Workshop Name:</div>
          <div className="inline-box value">
            {claimData?.workshop_name || "--"}
          </div>
        </div>

        <div className="info-row">
          <div className="inline-box label">Registration No.:</div>
          <div className="inline-box value">
            {claimData?.vehicle_number || "--"}
          </div>

          <div className="inline-box label">Surveyor Name:</div>
          <div className="inline-box value">Surveyor's Ai</div>

          <div className="inline-box label">Workshop GST Number:</div>
          <div className="inline-box value">
            {claimData?.workshop_gst || "--"}
          </div>
        </div>

        <div className="info-row">
          <div className="inline-box label">Date of Reg.:</div>
          <div className="inline-box value">
            {formatDateTime(data?.DateofRegistration)}
          </div>

          <div className="inline-box label">Claim Coordinator:</div>
          <div className="inline-box value">
            {claimData?.claimCoordinator || "--"}
          </div>

          <div className="inline-box label">Date of Accident:</div>
          <div className="inline-box value">
            {formatDateTime(claimData?.accident_datetime)}
          </div>
        </div>

        <div className="info-row">
          <div className="inline-box label">Make & Model:</div>
          <div className="inline-box value">
            {claimData?.make || ""} {claimData?.model || "--"}{" "}
            {claimData?.variant || "--"}
          </div>

          <div className="inline-box label">Chassis No.:</div>
          <div className="inline-box value">
            {data?.ChassisNumber || claimData?.chassis_number || "--"}
          </div>

          <div className="inline-box label">Vehicle Age (Years):</div>
          <div className="inline-box value">
            {(() => {
              const regDateRaw = data?.DateofRegistration;
              const accidentRaw = claimData?.accident_datetime;

              if (regDateRaw && accidentRaw) {
                const regDate = new Date(regDateRaw);
                const accidentDate = new Date(accidentRaw);

                if (!isNaN(regDate) && !isNaN(accidentDate)) {
                  let years =
                    accidentDate.getFullYear() - regDate.getFullYear();
                  let months = accidentDate.getMonth() - regDate.getMonth();
                  let days = accidentDate.getDate() - regDate.getDate();

                  if (days < 0) {
                    months -= 1;
                    days += new Date(
                      accidentDate.getFullYear(),
                      accidentDate.getMonth(),
                      0,
                    ).getDate();
                  }

                  if (months < 0) {
                    years -= 1;
                    months += 12;
                  }

                  years = Math.max(years, 1);

                  return `${years} Years ${months} Months`;
                }
              }

              return "--";
            })()}
          </div>
        </div>

        <div className="info-row">
          <div className="inline-box label">Payment to:</div>
          <div className="inline-box value">Workshop</div>
        </div>
      </div>

      {/* FOR MOBILE VIEW SMALL SECTION ADDED */}
      <div className="mobile-only">
        <div className="report-box">
          <div className="row">
            <span className="label">Policy Number</span>
            <span className="value">{data.policyNumber}</span>
          </div>
          <div className="row">
            <span className="label">Claim Number</span>
            <span className="value">{data.claimNumber}</span>
          </div>
          <div className="row">
            <span className="label">Ticket Number</span>
            <span className="value">--</span>
          </div>
        </div>
      </div>

      {/* === MOBILE VERSION: INFO BOX === */}
      <div className="mobile-only">
        <div className="report-box">
          <div className="row">
            <span className="label">Date</span>
            <span className="value">{formatDateTime(new Date())}</span>
          </div>
          <div className="row">
            <span className="label">Assessment Date</span>
            <span className="value">{data.date}</span>
          </div>
          <div className="row">
            <span className="label">Name of Owner</span>
            <span className="value">{data.insured}</span>
          </div>
          <div className="row">
            <span className="label">Engine No.</span>
            <span className="value">{data.EngineNumber}</span>
          </div>
          <div className="row">
            <span className="label">Workshop Name</span>
            <span className="value">{data.hpa}</span>
          </div>
          <div className="row">
            <span className="label">Registration No.</span>
            <span className="value">{data.VehicleRegistrationNumber}</span>
          </div>
          <div className="row">
            <span className="label">Surveyor Name</span>
            <span className="value">Surveyor AI</span>
          </div>
          <div className="row">
            <span className="label">Workshop GST Number</span>
            <span className="value">--</span>
          </div>
          <div className="row">
            <span className="label">Date of Reg.</span>
            <span className="value">
              {formatDateTime(data.DateofRegistration)}
            </span>
          </div>
          <div className="row">
            <span className="label">Claim Coordinator</span>
            <span className="value">--</span>
          </div>
          <div className="row">
            <span className="label">Date of Accident</span>
            <span className="value">{formatDateTime(data.dateOfAccident)}</span>
          </div>
          <div className="row">
            <span className="label">Make & Model</span>
            <span className="value">
              {data.Make} {data.Variant}
            </span>
          </div>
          <div className="row">
            <span className="label">Chassis No.</span>
            <span className="value">{data.ChassisNumber}</span>
          </div>
          <div className="row">
            <span className="label">Vehicle Age (Years)</span>
            <span className="value">--</span>
          </div>
          <div className="row">
            <span className="label">Payment to</span>
            <span className="value">Workshop</span>
          </div>
        </div>
      </div>

      {/* SMALL PART ENDED HERE */}

      <div className="note-section">
        <p>
          "Please find the below tentative estimation for the subject vehicle as
          per the visible damages found on the vehicle. Any further
          replacement/repair of part required during the course of repair shall
          be verified and included in the estimation. We request you to take
          prior approval from the insurer for all kept open part approval, if
          any. Any further replacement/repair of part required during the course
          of repair shall be verified and included in the assessment subject to
          the admissibility of the claim. We request you to take prior approval
          from the insurer for all parts put on hold and are pending for
          approval."
        </p>
        <p>
          <strong>Important Notes:</strong>
        </p>
        <ul>
          <li>
            After completion of repair, you shall submit the vehicle for
            reinspection and also produce the salvage of parts replaced.
          </li>
          <li>
            Please have the final tax invoice raised in favor of Surveyor
            Insurance Limited up to the Insurer liability, and the remaining
            balance to be invoiced in favor of the Insured.
          </li>
        </ul>
        <p className="help-note">
          For any further clarifications regarding your claim: Contact TPA
          at&nbsp;
          {/* {formData.email_id || ' support@tpa.com'} or{' '}
          {formData.phone_number || ' +91-XXXXX-XXXXX'} */}
          {"surveyor@surveyor.com"} or {" +91-9876543210"}
        </p>
      </div>

      <h4 className="section-heading">PARTS</h4>

      <table className="parts-table">
        <thead>
          <tr>
            <th>SN</th>
            <th>JOB</th>
            <th>MATERIAL</th>
            <th>DESCRIPTION</th>
            <th>HSN</th>
            <th>QTY</th>
            <th>PART RATE</th>
            <th>TAXABLE AMOUNT</th>
            <th>TAX (%)</th>
            <th>TAX AMOUNT</th>
            <th>TOTAL AMOUNT</th>
            <th>DEP (%)</th>
            <th>DEP AMOUNT</th>
            <th>R&R CHARGES</th>
            <th>REPAIR CHARGES</th>
            <th>PAINTING CHARGES</th>
          </tr>
        </thead>
        <tbody>
          {data.parts &&
            data.parts.map((part, i) => {
              const qty = Number(part.qty) || 0;
              const rate = Number(part.rate) || 0;
              const taxPercent = Number(part.taxPercent) || 0;
              const depPercent = Number(part.depPercent) || 0;

              const taxableAmount = qty * rate;
              const taxAmount = (taxableAmount * taxPercent) / 100;
              const depAmount = (taxableAmount * depPercent) / 100;
              const totalAmount = taxableAmount + taxAmount;

              return (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{part.decision}</td>
                  <td>{part.material}</td>
                  <td>{part.description}</td>
                  <td>{part.hsn}</td>
                  <td>{qty}</td>
                  <td>{rate}</td>
                  <td>₹{taxableAmount.toFixed(2)}</td>
                  <td>{taxPercent}</td>
                  <td>₹{taxAmount.toFixed(2)}</td>
                  <td>₹{totalAmount.toFixed(2)}</td>
                  <td>{depPercent}</td>
                  <td>₹{depAmount.toFixed(2)}</td>
                  <td>₹{part.rrCharges ?? 0}</td>
                  <td>₹{part.repairCharges ?? 0}</td>
                  <td>₹{part.paintingCharges ?? 0}</td>
                </tr>
              );
            })}

          {data?.parts && (
            <tr className="subtotal-row">
              <td colSpan="6">Subtotal</td>
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => sum + Number(p.qty) * Number(p.rate), 0)
                  .toFixed(2)}
              </td>
              <td></td>
              <td></td>
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const base = Number(p.qty) * Number(p.rate);
                    return sum + (base * Number(p.taxPercent)) / 100;
                  }, 0)
                  .toFixed(2)}
              </td>
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const base = Number(p.qty) * Number(p.rate);
                    const tax = (base * Number(p.taxPercent)) / 100;
                    return sum + base + tax;
                  }, 0)
                  .toFixed(2)}
              </td>
              <td></td>
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const base = Number(p.qty) * Number(p.rate);
                    return sum + (base * Number(p.depPercent)) / 100;
                  }, 0)
                  .toFixed(2)}
              </td>
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const rrCharges = Number(p.rrCharges) || 0;
                    return sum + rrCharges;
                  }, 0)
                  .toFixed(2)}
              </td>

              {/* REPAIR CHARGES total */}
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const repairCharges = Number(p.repairCharges) || 0;
                    return sum + repairCharges;
                  }, 0)
                  .toFixed(2)}
              </td>

              {/* PAINTING CHARGES total */}
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const paintingCharges = Number(p.paintingCharges) || 0;
                    return sum + paintingCharges;
                  }, 0)
                  .toFixed(2)}
              </td>
              <td colSpan="5"></td>
            </tr>
          )}
        </tbody>
      </table>

      <table className="labor-table">
        <thead>
          <tr>
            <th>LABOUR TYPE</th>
            <th>TAXABLE AMOUNT</th>
            <th>TAX AMOUNT</th>
            <th>TOTAL AMOUNT</th>
            <th>DEP AMOUNT</th>
          </tr>
        </thead>

        {/* <tbody>
          {data?.labourDetails?.length > 0 ? (
            data.labourDetails.map((labour, i) => (
              <tr key={i}>
                <td>{labour.labourType || '-'}</td>
                <td>
                  {labour.taxableAmount && labour.taxableAmount !== 'Not Found'
                    ? `₹${Number(labour.taxableAmount).toFixed(2)}`
                    : '-'}
                </td>
                <td>
                  {labour.taxAmount && labour.taxAmount !== 'Not Found'
                    ? `₹${Number(labour.taxAmount).toFixed(2)}`
                    : '-'}
                </td>
                <td>
                  {labour.totalAmount && labour.totalAmount !== 'Not Found'
                    ? `₹${Number(labour.totalAmount).toFixed(2)}`
                    : '-'}
                </td>
                <td>
                  {labour.depAmount && labour.depAmount !== 'Not Found'
                    ? `₹${Number(labour.depAmount).toFixed(2)}`
                    : '-'}
                </td>
              </tr>
            ))
          ) : (
            <>
              <tr>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
              </tr>
              <tr>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
              </tr>
            </>
          )}
        </tbody> */}
        {/* <tbody>
          {labourTypes.map((item, i) => {
            const taxable = getSubtotal(item.key);
            const tax = (taxable * 18) / 100;
            const total = taxable + tax;
            const dep = getDepAmount(item.key);

            return (
              <tr key={i}>
                <td>{item.label}</td>
                <td>₹{taxable.toFixed(2)}</td>
                <td>₹{tax.toFixed(2)}</td>
                <td>₹{total.toFixed(2)}</td>
                <td>₹{dep.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody> */}
        <tbody>
          {labourTypes.map((item, i) => {
            const taxable = getSubtotal(item.key);
            const tax = (taxable * 18) / 100;
            // const total = taxable + tax;

            const apiLabour = getLabourFromAPI(item.label);

            const dep =
              apiLabour?.depAmount && apiLabour.depAmount !== "Not Found"
                ? Number(apiLabour.depAmount)
                : 0;
            const total = taxable + tax - dep;
            return (
              <tr key={i}>
                <td>{item.label}</td>
                <td>₹{taxable.toFixed(2)}</td>
                <td>₹{tax.toFixed(2)}</td>
                <td>₹{total.toFixed(2)}</td>
                <td>₹{dep.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <table className="final-table">
        <thead>
          <tr>
            <th>FINAL CALCULATION</th>
            <th>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {/* <tr>
            <td>Total Liability</td>
            <td>
              {data?.finalCalculation?.totalLiability &&
              data.finalCalculation.totalLiability !== "Not Found"
                ? `₹${Number(data.finalCalculation.totalLiability).toFixed(2)}`
                : "--"}
            </td>
          </tr> */}
          <tr>
            <td>Total Liability</td>
            <td>₹{totalLiability.toFixed(2)}</td>
          </tr>
          <tr>
            <td>Compulsory Deductible</td>
            <td>
              {data?.finalCalculation?.compulsoryDeductible &&
              data.finalCalculation.compulsoryDeductible !== "Not Found"
                ? `₹${Number(
                    data.finalCalculation.compulsoryDeductible,
                  ).toFixed(2)}`
                : "--"}
            </td>
          </tr>
          <tr>
            <td>Voluntary Deductible</td>
            <td>
              {data?.finalCalculation?.voluntaryDeductible &&
              data.finalCalculation.voluntaryDeductible !== "Not Found"
                ? `₹${Number(data.finalCalculation.voluntaryDeductible).toFixed(
                    2,
                  )}`
                : "--"}
            </td>
          </tr>
          <tr>
            <td>Salvage Deductions</td>
            <td>
              {data?.finalCalculation?.salvageDeductions &&
              data.finalCalculation.salvageDeductions !== "Not Found"
                ? `₹${Number(data.finalCalculation.salvageDeductions).toFixed(
                    2,
                  )}`
                : "--"}
            </td>
          </tr>
          <tr>
            <td>Non-Standard Amount</td>
            <td>
              {data?.finalCalculation?.nonStandardAmount &&
              data.finalCalculation.nonStandardAmount !== "Not Found"
                ? `₹${Number(data.finalCalculation.nonStandardAmount).toFixed(
                    2,
                  )}`
                : "--"}
            </td>
          </tr>
          <tr>
            <td>Towing Amount</td>
            <td>
              {data?.finalCalculation?.towingAmount &&
              data.finalCalculation.towingAmount !== "Not Found"
                ? `₹${Number(data.finalCalculation.towingAmount).toFixed(2)}`
                : "--"}
            </td>
          </tr>
          <tr>
  <td>Depreciation Amount</td>
  <td>₹{totalDepreciation.toFixed(2)}</td>
</tr>

<tr>
  <td>Nil Depreciation Amount</td>
  <td>₹{nilDepreciationAmount.toFixed(2)}</td>
</tr>
          <tr className="total-row">
            <td>Insurer Liability</td>
            {/* <td>
              {data?.finalCalculation?.digitLiability &&
              data.finalCalculation.digitLiability !== "Not Found"
                ? `₹${Number(data.finalCalculation.digitLiability).toFixed(2)}`
                : "--"}
            </td> */}
            <td>₹{insurerLiability.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      {/* === MOBILE VERSION OF PARTS === */}
      <div className="mobile-only">
        <h4 className="section-heading">PARTS</h4>

        {data?.parts?.map((part, i) => (
          <div key={i} className="report-box">
            <div className="row sn-row">
              <span className="label">SN</span>
              <span className="value">{i + 1}</span>
            </div>
            <div className="row">
              <span className="label">Job</span>
              <span className="value">{part.decision || "-"}</span>
            </div>
            <div className="row">
              <span className="label">Material</span>
              <span className="value">{part.material || "-"}</span>
            </div>
            <div className="row">
              <span className="label">Description</span>
              <span className="value">{part.description || "-"}</span>
            </div>
            <div className="row">
              <span className="label">HSN</span>
              <span className="value">{part.hsn || "-"}</span>
            </div>
            <div className="row">
              <span className="label">Qty</span>
              <span className="value">{part.qty || "0"}</span>
            </div>
            <div className="row">
              <span className="label">Rate</span>
              <span className="value">
                ₹{Number(part.rate || 0).toFixed(2)}
              </span>
            </div>
            <div className="row">
              <span className="label">Taxable Amount</span>
              <span className="value">
                ₹{Number(part.taxableAmount || 0).toFixed(2)}
              </span>
            </div>
            <div className="row">
              <span className="label">Tax %</span>
              <span className="value">{part.taxPercent || "0"}%</span>
            </div>
            <div className="row">
              <span className="label">Tax Amount</span>
              <span className="value">
                ₹{Number(part.taxAmount || 0).toFixed(2)}
              </span>
            </div>
            <div className="row">
              <span className="label">Total Amount</span>
              <span className="value">
                ₹{Number(part.totalAmount || 0).toFixed(2)}
              </span>
            </div>
            <div className="row">
              <span className="label">Dep %</span>
              <span className="value">{part.depPercent || "0"}%</span>
            </div>
            <div className="row">
              <span className="label">Dep Amount</span>
              <span className="value">
                ₹{Number(part.depAmount || 0).toFixed(2)}
              </span>
            </div>
            <div className="row">
              <span className="label">R&R Charges</span>
              <span className="value">
                ₹{Number(part.rrCharges || 0).toFixed(2)}
              </span>
            </div>
            <div className="row">
              <span className="label">Repair Charges</span>
              <span className="value">
                ₹{Number(part.repairCharges || 0).toFixed(2)}
              </span>
            </div>
            <div className="row">
              <span className="label">Painting Charges</span>
              <span className="value">
                ₹{Number(part.paintingCharges || 0).toFixed(2)}
              </span>
            </div>
          </div>
        ))}

        {/* LABOUR SECTION */}
        <h4 className="section-heading">LABOUR CHARGES</h4>
        <div className="report-box">
          {data?.labourDetails?.length > 0 ? (
            data.labourDetails.map((labour, i) => (
              <React.Fragment key={i}>
                <div className="row">
                  <span className="label">Labour Type</span>
                  <span className="value">{labour.labourType || "-"}</span>
                </div>
                <div className="row">
                  <span className="label">Taxable Amount</span>
                  <span className="value">
                    ₹{Number(labour.taxableAmount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="row">
                  <span className="label">Tax Amount</span>
                  <span className="value">
                    ₹{Number(labour.taxAmount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="row">
                  <span className="label">Total Amount</span>
                  <span className="value">
                    ₹{Number(labour.totalAmount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="row">
                  <span className="label">Dep Amount</span>
                  <span className="value">
                    ₹{Number(labour.depAmount || 0).toFixed(2)}
                  </span>
                </div>
              </React.Fragment>
            ))
          ) : (
            <>
              <div className="row">
                <span className="label">Labour Type</span>
                <span className="value">-</span>
              </div>
              <div className="row">
                <span className="label">Taxable Amount</span>
                <span className="value">-</span>
              </div>
              <div className="row">
                <span className="label">Tax Amount</span>
                <span className="value">-</span>
              </div>
              <div className="row">
                <span className="label">Total Amount</span>
                <span className="value">-</span>
              </div>
              <div className="row">
                <span className="label">Dep Amount</span>
                <span className="value">-</span>
              </div>
            </>
          )}
        </div>

        {/* SUB TOTAL HERE */}
        <h4 className="section-heading">SUBTOTAL</h4>
        <div className="report-box subtotal-box">
          <div className="row total">
            <span className="label">Subtotal (Taxable)</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => sum + (Number(p.taxableAmount) || 0), 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Total Tax</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => sum + (Number(p.taxAmount) || 0), 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Grand Total</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => sum + (Number(p.totalAmount) || 0), 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Total Depreciation</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => sum + (Number(p.depAmount) || 0), 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Total R&R Charges</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => {
                  const rrCharges = Number(p.rrCharges) || 0;
                  return sum + rrCharges;
                }, 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Total Repair Charges</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => {
                  const repairCharges = Number(p.repairCharges) || 0;
                  return sum + repairCharges;
                }, 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Total Painting Charges</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => {
                  const paintingCharges = Number(p.paintingCharges) || 0;
                  return sum + paintingCharges;
                }, 0)
                .toFixed(2)}
            </span>
          </div>
        </div>

        {/* FINAL CALCULATION */}
        <h4 className="section-heading">FINAL CALCULATION</h4>
        <div className="report-box">
          <div className="row">
            <span className="label">Total Liability</span>
            <span className="value">
              {data?.finalCalculation?.totalLiability
                ? `₹${Number(data.finalCalculation.totalLiability).toFixed(2)}`
                : "--"}
            </span>
          </div>
          <div className="row">
            <span className="label">Compulsory Deductible</span>
            <span className="value">
              {data?.finalCalculation?.compulsoryDeductible
                ? `₹${Number(
                    data.finalCalculation.compulsoryDeductible,
                  ).toFixed(2)}`
                : "--"}
            </span>
          </div>
          <div className="row">
            <span className="label">Voluntary Deductible</span>
            <span className="value">
              {data?.finalCalculation?.voluntaryDeductible
                ? `₹${Number(data.finalCalculation.voluntaryDeductible).toFixed(
                    2,
                  )}`
                : "--"}
            </span>
          </div>
          <div className="row">
            <span className="label">Salvage Deductions</span>
            <span className="value">
              {data?.finalCalculation?.salvageDeductions
                ? `₹${Number(data.finalCalculation.salvageDeductions).toFixed(
                    2,
                  )}`
                : "--"}
            </span>
          </div>
          <div className="row">
            <span className="label">Non-Standard Amount</span>
            <span className="value">
              {data?.finalCalculation?.nonStandardAmount
                ? `₹${Number(data.finalCalculation.nonStandardAmount).toFixed(
                    2,
                  )}`
                : "--"}
            </span>
          </div>
          <div className="row">
            <span className="label">Towing Amount</span>
            <span className="value">
              {data?.finalCalculation?.towingAmount
                ? `₹${Number(data.finalCalculation.towingAmount).toFixed(2)}`
                : "--"}
            </span>
          </div>
          <div className="row total">
            <span className="label">Insurer Liability</span>
            <span className="value">
              {data?.finalCalculation?.digitLiability
                ? `₹${Number(data.finalCalculation.digitLiability).toFixed(2)}`
                : "--"}
            </span>
          </div>
        </div>
      </div>

      <div className="note-section">
        <div className="section-heading">ASSESSMENT NOTES</div>
        <ul className="assessment-notes">
          <li>
            Any additional amount over and above the approved assessment shall
            be borne by the Insured.
          </li>
          <li>
            The final liability has been arrived at as full and final settlement
            after consent from the Insured. Do get back to us within the next 24
            hours for any queries or clarifications.
          </li>
        </ul>
      </div>

      <div className="button-group">
        <button
          className="approve-btn"
          onClick={handleApproveClick}
          disabled={downloading}
        >
          {downloading ? (
            <>
              <div className="loading-spinner-small"></div>
              Processing...
            </>
          ) : (
            <>
              Approve
              <FontAwesomeIcon icon={faCheck} style={{ marginLeft: "12px" }} />
            </>
          )}
        </button>
        <button className="manual-btn" onClick={handleManualqcClick}>
          Manual QC{" "}
          <FontAwesomeIcon icon={faPen} style={{ marginLeft: "12px" }} />
        </button>
      </div>
    </div>
  );
};

export default InitialAssessmentReport;
